/*
 * ****************************************************************************
 * Copyright (C) 2018-2018, All rights reserved.
 * Project: chessmate
 * Created On: March 23rd, 2018
 * Last Modified: March 23rd, 2018
 * ****************************************************************************
 */

import * as React from 'react';
import * as Mui from 'mui';
import { AppEvents } from 'contexts/appEventsContext';

/** ClipboardProps - Properties */
export interface ClipboardProps {
  value: string;
  events: AppEvents;
  onPaste?: (arg: { value: string, source: string }) => void;
}

export interface ClipboardState {
  pasted?: boolean;
  message?: string;
  showPaste?: boolean;
  pasteText?: string;
  showCopy?: boolean;
  unsubscribe?: Array<() => void>;
}

const edit_style: React.CSSProperties = {
  position: 'absolute',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  height: 1,
  width: 1,
  margin: -1,
  padding: 0,
  border: 0
};

/** Clipboard */
export class Clipboard extends React.Component<ClipboardProps, ClipboardState> {
  private readonly _editref = React.createRef<HTMLTextAreaElement>();

  constructor(props: ClipboardProps, ctx: any) {
    super(props, ctx);
    this.state = { pasted: false };
  }

  componentDidMount() {
    const subscribers: any = [
      ['keydown', this.handleKeyDown],
      ['keyup', this.handleKeyUp],
      ['copy', this.handleDocCopy],
      ['paste', this.handleDocPaste],
    ];

    const unsubscribe: Array<() => void> = [];
    for (const item of subscribers) {
      const [name, handler] = item;
      document.addEventListener(name, handler, false);
      unsubscribe.push(() => document.removeEventListener(name, handler, false));
    }
    unsubscribe.push(this.props.events.onMenu('copy', this.onMenuCopy));
    unsubscribe.push(this.props.events.onMenu('paste', this.onMenuPaste));
    this.setState({ unsubscribe });
  }

  componentWillUnmount() {
    const all = this.state.unsubscribe || [];
    for (const unsub of all) {
      unsub();
    }
  }

  render() {
    const { onPaste } = this.props;
    return (
      <React.Fragment>
        <textarea id="clipboardEdit"
          ref={this._editref}
          value={this.props.value}
          style={edit_style}
          onCopy={this.handleCopy}
          onPaste={this.handlePaste}
          onChange={this.handleChange}
        />
        {!!(this.state.showPaste && onPaste) && (
          <Mui.Dialog open onClose={() => this.setState({ showPaste: false })}>
            <Mui.Paper style={{ padding: '8px' }}>
              <Mui.Typography variant="body1" color="primary" component="div" className="col-1">
                <Mui.Input
                  id="pasteValue"
                  type="textarea"
                  multiline
                  rows="8"
                  margin="dense"
                  placeholder="(Paste here)"
                  value={this.state.pasteText}
                  onChange={e => this.setState({ pasteText: e.target.value })}
                  inputProps={{ style: { minHeight: '100px', minWidth: '200px' } }}
                />
                <div className="center mt-8">
                  <Mui.Button variant="raised" color="primary" onClick={() => {
                    onPaste({ value: this.state.pasteText || '', source: 'modal' });
                    this.setState({ showPaste: false, pasteText: '' });
                  }}>
                    <Mui.Icon>save_alt</Mui.Icon>Apply
                  </Mui.Button>
                </div>
              </Mui.Typography>
            </Mui.Paper>
          </Mui.Dialog>
        )}
        {!!(this.state.showCopy && this.props.value) && (
          <Mui.Dialog open onClose={() => this.setState({ showCopy: false })}>
            <Mui.Paper style={{ padding: '8px' }}>
              <Mui.Typography variant="body1" color="primary" component="div" className="col-1">
                <Mui.Input
                  id="copyValue"
                  type="textarea"
                  multiline
                  rows="8"
                  margin="dense"
                  placeholder="(Paste here)"
                  value={this.props.value}
                  onChange={() => { }}
                  inputProps={{ style: { minHeight: '100px', minWidth: '200px' } }}
                />
                <div className="center mt-8">
                  <Mui.Button variant="raised" color="primary" onClick={() => {
                    this.setState({ showCopy: false });
                  }}>
                    <Mui.Icon>keyboard_return</Mui.Icon>Return
                  </Mui.Button>
                </div>
              </Mui.Typography>
            </Mui.Paper>
          </Mui.Dialog>
        )}
        {!!(this.state.message) && (
          <Mui.Snackbar
            open
            autoHideDuration={5000}
            onClose={() => this.setState({ message: undefined })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            message={<span>{this.state.message}</span>}
          />
        )}
      </React.Fragment>
    );
  }

  onMenuPaste = () => {
    const element = this._editref.current;
    const { onPaste } = this.props;
    if (!element || !onPaste) {
      return;
    }
    const { clipboard } = window.navigator as any;
    if (clipboard && clipboard.writeText) {
      (clipboard.readText() as Promise<string>)
        .then((value) => {
          onPaste({ value, source: 'clipboard' });
        })
        .catch(ex => {
          console.error(ex);
          alert('Failed to copy.');
        });
    }
    else {
      this.setState({ showPaste: true, pasteText: '' });
    }
  };

  onMenuCopy = () => {
    const element = this._editref.current;
    if (element) {
      const { clipboard } = window.navigator as any;
      if (clipboard && clipboard.writeText) {
        (clipboard.writeText(this.props.value) as Promise<void>)
          .then(() => {
            this.setState({ message: 'Copied to clipboard.' });
          })
          .catch(ex => {
            console.error(ex);
            alert('Failed to copy.');
          });
      }
      else {
        const isEnabled = document.queryCommandEnabled('Copy');
        const isSupported = document.queryCommandSupported('Copy');
        let success = isEnabled && isSupported;
        if (success) {
          element.focus();
          element.select();
          success = document.execCommand('Copy', false, null);
        }
        if (!success) {
          this.setState({ showCopy: true });
        }
      }
    }
  };

  handleDocCopy = (e: Event) => {
    const { clipboard } = window.navigator as any;
    if (this.state.showCopy) {
      return;
    }
    if (clipboard && clipboard.writeText) {
      e.preventDefault();
      (clipboard.writeText(this.props.value) as Promise<void>)
        .then(() => {
          this.setState({ message: 'Copied to clipboard.' });
        })
        .catch(ex => {
          console.error(ex);
          alert('Failed to copy.');
        });
    }
    //allow default...
  };

  handleDocPaste = (e: Event) => {
    const { clipboard } = window.navigator as any;
    const { onPaste } = this.props;
    if (!onPaste || this.state.showPaste) {
      return;
    }
    if (clipboard && clipboard.readText) {
      e.preventDefault();
      (clipboard.readText() as Promise<string>)
        .then(text => {
          onPaste({ value: text, source: 'clipboard' });
        })
        .catch(ex => {
          console.error(ex);
          alert('Failed to paste.');
        });
    }
    else {
      const clipData: any = (e as any).clipboardData;
      if (clipData) {
        const text = clipData.getData('text/plain');
        if (typeof text === 'string') {
          e.preventDefault();
          onPaste({ value: text, source: 'event' });
        }
      }
    }
  }

  handleCopy = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    this.setState({ message: 'Copied to clipboard.' });
  }

  handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const element = this._editref.current;
    if (element) {
      this.setState({ pasted: true });
    }
  }

  handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const element = this._editref.current;
    if (element && this.state.pasted && this.props.onPaste) {
      this.props.onPaste({ value: element.value, source: 'change' });
    }
    this.setState({ pasted: false });
  };

  handleKeyDown = (e: KeyboardEvent) => {
    const metaKeyIsDown = (e.ctrlKey || e.metaKey);
    const textIsSelected = window.getSelection().toString();

    if (!metaKeyIsDown || textIsSelected) {
      return;
    }

    const element = this._editref.current;
    if (element) {
      element.focus();
      element.select();
    }
  }

  handleKeyUp = (e: KeyboardEvent) => {
    const element = this._editref.current;
    if (element) {
      element.blur();
    }
  }
}

export default Clipboard;


// WEBPACK FOOTER //
// ./src/components/clipboard.tsx